import React, { useEffect, useMemo, useState } from 'react';
import {
    Author,
    createAuthor,
    deleteAuthor,
    fetchAllAuthors,
    getAuthorWithWorks,
    searchAuthorsApi,
    updateAuthor,
} from '../../../../api/authors';
import '../../../../../src/assets/styles/authors-manager.scss';

function displayAuthorName(a: Author) {
    if (a.first_name || a.last_name) return `${a.first_name ?? ''} ${a.last_name ?? ''}`.trim();
    return a.name ?? '(bez nazwy)';
}

export default function AuthorsManager() {
    const [authors, setAuthors] = useState<Author[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [works, setWorks] = useState<any[]>([]);
    const [isEditing, setIsEditing] = useState(false);

    // edycja wybranego
    const [form, setForm] = useState<Partial<Author>>({
        first_name: '', last_name: '', name: '', bio: '', country: '', birth_date: ''
    });

    // tworzenie
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState<Partial<Author>>({
        first_name: '', last_name: '', name: '', bio: '', country: '', birth_date: ''
    });

    // modal delete
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [toDelete, setToDelete] = useState<Author | null>(null);

    const load = async () => {
        setLoading(true); setError(null);
        try {
            const data = await fetchAllAuthors();
            setAuthors(data);
        } catch (e: any) {
            setError(e?.error || 'Nie udało się pobrać autorów');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        if (!s) return authors;
        return authors.filter(a =>
            displayAuthorName(a).toLowerCase().includes(s) ||
            (a.country ?? '').toLowerCase().includes(s) ||
            String(a.id).includes(s)
        );
    }, [authors, search]);

    const onToggleRow = async (a: Author) => {
        if (selectedId === a.id) {
            // zamknij
            setSelectedId(null);
            setWorks([]);
            setIsEditing(false);
            return;
        }
        setSelectedId(a.id);
        setIsEditing(false);
        setForm({ ...a });
        setLoadingDetails(true);
        try {
            const detailed = await getAuthorWithWorks(a.id);
            setWorks(detailed.works ?? []);
        } catch {
            setWorks([]);
        } finally {
            setLoadingDetails(false);
        }
    };

    const onSave = async () => {
        if (selectedId == null) return;
        try {
            const payload = { ...form };
            await updateAuthor(selectedId, payload);
            setAuthors(prev => prev.map(a => a.id === selectedId ? { ...a, ...payload } as Author : a));
            setIsEditing(false);
        } catch (e: any) {
            alert(e?.error || 'Błąd przy zapisie autora');
        }
    };

    const openDelete = (a: Author) => { setToDelete(a); setConfirmOpen(true); };
    const closeDelete = () => { setConfirmOpen(false); setToDelete(null); };

    const doDelete = async () => {
        if (!toDelete) return;
        try {
            await deleteAuthor(toDelete.id);
            setAuthors(prev => prev.filter(x => x.id !== toDelete.id));
            if (selectedId === toDelete.id) {
                setSelectedId(null); setWorks([]); setIsEditing(false);
            }
        } catch (e: any) {
            const msg = e?.error || e?.message || 'Błąd przy usuwaniu';
            if (/only owner|admins only|forbidden|not allowed|owner/i.test(msg)) {
                alert('Nie masz uprawnień do usunięcia tego autora.');
            } else if (/used by contents|409|conflict/i.test(msg)) {
                alert('Nie można usunąć – autor jest używany w treściach innych użytkowników.');
            } else {
                alert(msg);
            }
        } finally {
            closeDelete();
        }
    };

    const onCreate = async () => {
        const { first_name, last_name, name } = createForm;
        if (!first_name && !last_name && !name) {
            alert('Podaj imię/nazwisko lub nazwę');
            return;
        }
        try {
            const created = await createAuthor(createForm);
            setAuthors(prev => [created, ...prev]);
            setCreateForm({ first_name: '', last_name: '', name: '', bio: '', country: '', birth_date: '' });
            setCreateOpen(false);
            // rozwiń świeżo dodanego
            onToggleRow(created);
        } catch (e: any) {
            alert(e?.error || 'Błąd przy dodawaniu autora');
        }
    };

    return (
        <div className="authors-wrap">
            <div className="authors-head">
                <h2>Autorzy</h2>
                <div className="head-actions">
                    <div className="search-field">
                        <input
                            placeholder="Szukaj: imię, nazwisko, kraj, ID…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <button
                            className="search-btn"
                            onClick={async () => {
                                if (search.trim().length < 2) return;
                                try {
                                    const res = await searchAuthorsApi(search.trim());
                                    setAuthors(res);
                                } catch { }
                            }}
                            title="Szukaj w API"
                        >🔎</button>
                    </div>
                    <button className="btn-primary" onClick={() => setCreateOpen(v => !v)}>
                        {createOpen ? 'Zamknij' : 'Dodaj autora'}
                    </button>
                </div>
            </div>

            {createOpen && (
                <div className="create-card">
                    <h3>Nowy autor</h3>
                    <div className="form-grid">
                        <div className="fi">
                            <label>Imię</label>
                            <input value={createForm.first_name as string || ''} onChange={e => setCreateForm(f => ({ ...f, first_name: e.target.value }))} />
                        </div>
                        <div className="fi">
                            <label>Nazwisko</label>
                            <input value={createForm.last_name as string || ''} onChange={e => setCreateForm(f => ({ ...f, last_name: e.target.value }))} />
                        </div>
                        <div className="fi">
                            <label>Nazwa (gdy jedno pole)</label>
                            <input value={createForm.name as string || ''} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} />
                        </div>
                        <div className="fi">
                            <label>Kraj</label>
                            <input value={createForm.country as string || ''} onChange={e => setCreateForm(f => ({ ...f, country: e.target.value }))} />
                        </div>
                        <div className="fi">
                            <label>Data urodzenia</label>
                            <input type="date" value={createForm.birth_date as string || ''} onChange={e => setCreateForm(f => ({ ...f, birth_date: e.target.value }))} />
                        </div>
                        <div className="fi fi-col">
                            <label>Bio</label>
                            <textarea value={createForm.bio as string || ''} onChange={e => setCreateForm(f => ({ ...f, bio: e.target.value }))} />
                        </div>
                    </div>
                    <div className="actions-row">
                        <button className="btn-primary" onClick={onCreate}>Dodaj</button>
                        <button className="btn-ghost" onClick={() => setCreateOpen(false)}>Anuluj</button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="state muted">Ładowanie…</div>
            ) : error ? (
                <div className="state error">{error}</div>
            ) : (
                <div className="authors-table-wrapper">
                    <table className="authors-table" role="grid">
                        <thead>
                            <tr>
                                <th className="col-id">ID</th>
                                <th className="col-name">Imię i nazwisko</th>
                                <th className="col-country">Kraj</th>
                                <th className="col-created">Utworzył</th>
                                <th className="col-lastedit">Ostatnia edycja</th>
                                <th className="col-actions">Akcje</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(a => {
                                const open = selectedId === a.id;
                                return (
                                    <React.Fragment key={a.id}>
                                        <tr className={`row ${open ? 'open' : ''}`}>
                                            <td className="cell id">
                                                <span className="mono">{a.id}</span>
                                            </td>

                                            <td className="cell name">
                                                <button
                                                    className="linklike fullcell"
                                                    onClick={() => onToggleRow(a)}
                                                    aria-expanded={open}
                                                    aria-controls={`details-${a.id}`}
                                                >
                                                    {displayAuthorName(a)}
                                                </button>
                                            </td>

                                            <td className="cell country">{a.country || '—'}</td>

                                            <td className="cell created-by ellipsis" title={a.created_by_name || '—'}>
                                                {a.created_by_name || '—'}
                                            </td>

                                            <td
                                                className="cell last-edit ellipsis"
                                                title={
                                                    (a.last_edited_by_name ? `${a.last_edited_by_name} • ` : '') +
                                                    (a.last_edited_at ? new Date(a.last_edited_at).toLocaleString() : '—')
                                                }
                                            >
                                                {(a.last_edited_by_name || '—')}{' '}
                                                {a.last_edited_at ? `• ${new Date(a.last_edited_at).toLocaleString()}` : ''}
                                            </td>

                                            <td className="cell actions">
                                                {!open ? (
                                                    <>
                                                        <button
                                                            className="icon-btn edit"
                                                            onClick={() => { onToggleRow(a); setIsEditing(true); }}
                                                            aria-label="Edytuj"
                                                        >
                                                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M12 20h9"></path>
                                                                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
                                                            </svg>
                                                        </button>
                                                        <button className="icon-btn delete" onClick={() => openDelete(a)} aria-label="Usuń">
                                                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M3 6h18"></path>
                                                                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                                            </svg>
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button className="btn-ghost" onClick={() => onToggleRow(a)}>Zwiń</button>
                                                )}
                                            </td>
                                        </tr>

                                        {open && (
                                            <tr className="details-row">
                                                <td className="details-cell" colSpan={6} id={`details-${a.id}`}>
                                                    {loadingDetails ? (
                                                        <div className="state muted">Wczytywanie danych…</div>
                                                    ) : (
                                                        <div className="details-grid">
                                                            {/* KARTA INFO */}
                                                            <div className="card">
                                                                <h4 className="card-title">Informacje o autorze</h4>
                                                                {!isEditing ? (
                                                                    <>
                                                                        <dl className="kv-table" aria-label="Szczegóły autora">
                                                                            <div className="kv-pair">
                                                                                <dt className="kv-label">ID</dt>
                                                                                <dd className="kv-value mono">{a.id}</dd>
                                                                            </div>
                                                                            <div className="kv-pair">
                                                                                <dt className="kv-label">Imię i nazwisko</dt>
                                                                                <dd className="kv-value">{displayAuthorName(a)}</dd>
                                                                            </div>
                                                                            {a.name && (
                                                                                <div className="kv-pair">
                                                                                    <dt className="kv-label">Nazwa</dt>
                                                                                    <dd className="kv-value">{a.name}</dd>
                                                                                </div>
                                                                            )}
                                                                            {a.country && (
                                                                                <div className="kv-pair">
                                                                                    <dt className="kv-label">Kraj</dt>
                                                                                    <dd className="kv-value">{a.country}</dd>
                                                                                </div>
                                                                            )}
                                                                            {a.birth_date && (
                                                                                <div className="kv-pair">
                                                                                    <dt className="kv-label">Data urodzenia</dt>
                                                                                    <dd className="kv-value">{a.birth_date}</dd>
                                                                                </div>
                                                                            )}
                                                                            {a.bio && (
                                                                                <div className="kv-pair kv-span">
                                                                                    <dt className="kv-label">Bio</dt>
                                                                                    <dd className="kv-value">{a.bio}</dd>
                                                                                </div>
                                                                            )}
                                                                        </dl>

                                                                        {(a.created_by_name || a.last_edited_at) && (
                                                                            <div className="kv-meta">
                                                                                <div><b>Utworzył:</b> {a.created_by_name || '—'}</div>
                                                                                <div><b>Ostatnia edycja:</b> {a.last_edited_by_name ? `${a.last_edited_by_name} • ` : ''}{a.last_edited_at ? new Date(a.last_edited_at).toLocaleString() : '—'}</div>
                                                                            </div>
                                                                        )}

                                                                        <div className="actions-row">
                                                                            <button className="btn-primary" onClick={() => setIsEditing(true)}>Edytuj</button>
                                                                            <button className="btn-danger" onClick={() => openDelete(a)}>Usuń</button>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <div className="form-grid">
                                                                        <div className="fi">
                                                                            <label>Imię</label>
                                                                            <input value={form.first_name as string || ''} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
                                                                        </div>
                                                                        <div className="fi">
                                                                            <label>Nazwisko</label>
                                                                            <input value={form.last_name as string || ''} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
                                                                        </div>
                                                                        <div className="fi">
                                                                            <label>Nazwa</label>
                                                                            <input value={form.name as string || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                                                                        </div>
                                                                        <div className="fi">
                                                                            <label>Kraj</label>
                                                                            <input value={form.country as string || ''} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} />
                                                                        </div>
                                                                        <div className="fi">
                                                                            <label>Data urodzenia</label>
                                                                            <input type="date" value={form.birth_date as string || ''} onChange={e => setForm(f => ({ ...f, birth_date: e.target.value }))} />
                                                                        </div>
                                                                        <div className="fi fi-col">
                                                                            <label>Bio</label>
                                                                            <textarea value={form.bio as string || ''} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} />
                                                                        </div>

                                                                        <div className="actions-row">
                                                                            <button className="btn-primary" onClick={onSave}>Zapisz</button>
                                                                            <button className="btn-ghost" onClick={() => { setIsEditing(false); setForm({ ...a }); }}>Anuluj</button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* KARTA WORKS */}
                                                            <div className="card">
                                                                <h4 className="card-title">Treści autora</h4>
                                                                {works.length === 0 ? (
                                                                    <div className="state muted">Brak przypisanych treści.</div>
                                                                ) : (
                                                                    <ul className="works-list">
                                                                        {works.map((w: any) => (
                                                                            <li key={w.id}>
                                                                                <span className="title">{w.title}</span>
                                                                                <span className="type">({w.type})</span>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={6}>
                                        <div className="empty-table">Brak wyników.</div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal potwierdzenia */}
            {confirmOpen && toDelete && (
                <Dialog onClose={closeDelete}>
                    <div className="dialog-icon">⚠️</div>
                    <h3>Usunąć autora?</h3>
                    <p>„{displayAuthorName(toDelete)}” zostanie trwale usunięty.</p>
                    <div className="dialog-actions">
                        <button className="btn-danger" onClick={doDelete}>Usuń</button>
                        <button className="btn-ghost" onClick={closeDelete}>Anuluj</button>
                    </div>
                </Dialog>
            )}
        </div>
    );
}

/** Prosty modal */
function Dialog({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
    React.useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" aria-label="Zamknij" onClick={onClose}>×</button>
                {children}
            </div>
        </div>
    );
}
